import {ErrorMessage, Form, Formik} from 'formik'
import {isEmpty} from 'lodash'
import {useEffect} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {toast} from 'react-toastify'
import * as Yup from 'yup'
import {safeText} from 'src/cms/helpers/validation/safeText'

import {StateParamsModel} from 'src/app/modules/common/Model'
import * as memberType from '../index'

import Modal from 'rsuite/Modal'

import checkMultiLanguage from 'src/app/modules/common/checkMultiLanguage'
import FormRadio from 'src/cms/helpers/components/forms/FormRadio'
import FormTextBox from 'src/cms/helpers/components/forms/FormTextBox'
import FormTinyMce from 'src/cms/helpers/components/forms/FormTinyMce'

type Props = {
  open: boolean
  params?: StateParamsModel
  handleClose: () => void
  actionType: string
  editSelectedData?: any
  showManagementTabField?: boolean
}

const FORM_VALIDATION = Yup.object().shape({
  description: Yup.string().required('Description is required'),
  name: safeText().required('Name is required'),
  name_np: safeText().nullable(),
  status: Yup.string().required('Status is required'),
  show_in_management_tab: Yup.string().required('This field is required'),
})

const AddMemberType = ({open, params, handleClose, actionType, editSelectedData, showManagementTabField}: Props) => {
  const dispatch = useDispatch()
  const {loading, success} = useSelector((state: any) => state.memberType)

  const statusOptions = [
    {label: 'Active', value: 'Active'},
    {label: 'Inactive', value: 'Inactive'},
  ]

  const showInManagementTabOptions = [
    {label: 'Yes', value: 'Yes'},
    {label: 'No', value: 'No'},
  ]

  useEffect(() => {
    if (success) {
      dispatch(memberType?.actions.getMemberType(params))
      isEmpty(editSelectedData)
        ? toast.success('Member type added successfully')
        : toast.success('Member type edited successfully')
      dispatch(memberType?.actions?.resetMemberType())
      handleClose()
    }
  }, [success])
  const isSingleLanguage = checkMultiLanguage()
  return (
    <div className='modal-container' data-cy='modal-member-type-container'>
      <Modal
        open={open}
        onClose={handleClose}
        className='w-75'
        backdrop='static'
        keyboard={false}
        enforceFocus={false}
        data-cy='modal'
      >
        <Modal.Header>
          <Modal.Title data-cy='modal-title'>{actionType} Member Type</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div>
            <Formik
              initialValues={{
                name: '',
                description: '',
                slug: '',
                status: '',
                show_in_management_tab: 'No',
              }}
              validationSchema={FORM_VALIDATION}
              onSubmit={(values) => {
                const formData = {
                  ...values,
                  slug: !isEmpty(editSelectedData)
                    ? editSelectedData?.slug
                    : values?.name.replace(/\s/g, '-').toLowerCase(),
                  status: values?.status === 'Active' ? true : false,
                  show_in_management_tab: values?.show_in_management_tab === 'Yes' ? true : false,
                }

                if (!isEmpty(editSelectedData)) {
                  dispatch(memberType.actions.updateMemberType(formData, editSelectedData?.id))
                } else {
                  dispatch(memberType.actions.addMemberType(formData))
                }
              }}
            >
              {({touched, handleChange, errors, values, setFieldValue}) => {
                useEffect(() => {
                  if (!isEmpty(editSelectedData)) {
                    const fields = [
                      'description',
                      'description_np',
                      'slug',
                      'id',
                      'name',
                      'name_np',
                    ]
                    fields.forEach((field) => setFieldValue(field, editSelectedData[field], false))
                    setFieldValue(
                      'status',
                      editSelectedData?.status === true ? 'Active' : 'Inactive',
                      false
                    )
                    setFieldValue(
                      'show_in_management_tab',
                      editSelectedData?.show_in_management_tab === true ? 'Yes' : 'No',
                      false
                    )
                  }
                }, [])

                return (
                  <Form>
                    <div className='card-body border-top pt-5' data-cy='card-member-body'>
                      <div className='row'>
                        <div className='col-md-6 col-xs-12'>
                          <FormTextBox
                            type='text'
                            labelClassName='col-md-12'
                            containerClassName='col-md-12'
                            placeholder='Enter Name'
                            label={isSingleLanguage ? 'Name' : 'Name'}
                            name='name'
                            onChange={handleChange}
                            errors={errors}
                            touched={touched}
                            required
                            data-cy='add-member-type-name-text-field'
                          />
                        </div>
                        {!isSingleLanguage && (
                          <div className='col-md-6 col-xs-12'>
                            <FormTextBox
                              type='text'
                              labelClassName='col-md-12'
                              containerClassName='col-md-12'
                              placeholder='Name (NP)'
                              label='Name (NP)'
                              name='name_np'
                              onChange={handleChange}
                              errors={errors}
                              touched={touched}
                              data-cy='add-member-type-np-name-text-field'
                            />
                          </div>
                        )}

                        <div className='col-md-6 col-xs-12' data-cy='member-type-row-text-editor'>
                          <FormTinyMce
                            containerClassName='col-md-12'
                            label={isSingleLanguage ? 'Description' : 'Description (EN)'}
                            name='description'
                            initialValue={
                              !isEmpty(editSelectedData) ? editSelectedData?.description : ''
                            }
                            handleChange={handleChange}
                            required
                            data-cy='member-type-editor-message'
                          />
                        </div>
                        {!isSingleLanguage && (
                          <div
                            className='col-md-6 col-xs-12'
                            data-cy='member-type-row-np-text-editor'
                          >
                            <FormTinyMce
                              containerClassName='col-md-12'
                              label='Description (NP)'
                              name='description_np'
                              initialValue={
                                !isEmpty(editSelectedData) ? editSelectedData?.description_np : ''
                              }
                              handleChange={handleChange}
                              data-cy='member-editor-np-message'
                            />
                          </div>
                        )}

                        <div className='col-md-6 col-xs-12'>
                          <FormTextBox
                            type='text'
                            labelClassName='col-md-12'
                            containerClassName='col-md-12'
                            placeholder='Enter Slug'
                            label='Slug'
                            name='slug'
                            onChange={handleChange}
                            errors={errors}
                            touched={touched}
                            disabled={true}
                            value={
                              !isEmpty(editSelectedData)
                                ? editSelectedData?.slug
                                : values?.name.replace(/\s/g, '-').toLowerCase()
                            }
                            data-cy='add-member-type-slug-text-field'
                          />
                        </div>

                        <div className='col-md-6 col-xs-12'>
                          <label className='form-label fw-bolder text-dark fs-6 required'>
                            Status
                          </label>

                          <div className='d-flex ms-5'>
                            {statusOptions?.map((status: {label: string; value: string}) => (
                              <FormRadio
                                containerClassName=''
                                label='Select Status'
                                name='status'
                                type='radio'
                                onChange={handleChange}
                                errors={errors}
                                touched={touched}
                                required={true}
                                checkBoxText={status?.label}
                                value={status?.value}
                                data-cy='add-member-type-status-radio-button'
                              />
                            ))}
                          </div>
                          <div className='fv-plugins-message-container text-danger fw-bolder small text-end mt-1'>
                            <ErrorMessage
                              name='status'
                              component='div'
                              className='field-error-message'
                            />
                          </div>
                        </div>

                        {showManagementTabField && (
                          <div className='col-md-6 col-xs-12'>
                            <label className='form-label fw-bolder text-dark fs-6 required'>
                              Show in Management Team Page
                            </label>
                            <div className='d-flex ms-5'>
                              {showInManagementTabOptions?.map(
                                (option: {label: string; value: string}) => (
                                  <FormRadio
                                    containerClassName=''
                                    label='Show in Management Team Page'
                                    name='show_in_management_tab'
                                    type='radio'
                                    onChange={handleChange}
                                    errors={errors}
                                    touched={touched}
                                    required={true}
                                    checkBoxText={option?.label}
                                    value={option?.value}
                                    data-cy='add-member-type-show-in-management-tab-radio-button'
                                  />
                                )
                              )}
                            </div>
                            <div className='fv-plugins-message-container text-danger fw-bolder small text-end mt-1'>
                              <ErrorMessage
                                name='show_in_management_tab'
                                component='div'
                                className='field-error-message'
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className='d-flex justify-content-end px-9 '>
                      <button
                        type='submit'
                        disabled={loading}
                        className='btn btn-primary btn-sm ms-3'
                        data-cy='modal-submit'
                      >
                        Save
                      </button>
                      <button
                        type='button'
                        onClick={handleClose}
                        className='btn btn-secondary btn-sm ms-3'
                        data-cy='modal-cancel'
                      >
                        Cancel
                      </button>
                    </div>
                  </Form>
                )
              }}
            </Formik>
          </div>
        </Modal.Body>
      </Modal>
    </div>
  )
}
export default AddMemberType
